import layout from './CoreFrameDropDownTreeView.html?raw';
import ComponentManager from '@/Management/ComponentManager';
import Control from '@/Controls/Control';
import CoreFrameDropDownTreeViewViewModel from '@/ViewModels/Controls/CoreFrameDropDownTreeViewViewModel';
import FancytreeControlType from '@/Controls/FancyTreeControl';
import CheckBoxControlType from '@/Controls/CheckBox';
import CheckBoxViewModelT from '@/ViewModels/Controls/CheckBoxViewModel';
import KendoDropDownHelper from '@/Utility/KendoDropDownHelper';
import LocatorHelper from '@/Utility/LocatorHelper';
import Utilities from '@/Utility/Utilities';

const CONTROL_NAME = 'coreframedropdowntreeview';

export default class CoreFrameDropDownTreeView extends Control {
    private CheckBoxControl = ComponentManager.getControl<typeof CheckBoxControlType>('CheckBox');

    public fancyTreeControl: FancytreeControlType = null;
    private control: JQuery;
    private $wrapper: JQuery;
    private $popup: JQuery;
    private inRemovalState: boolean = false;
    private $fancytreeContainer: JQuery;

    private lastAction: string; // indicating a state for keyboard navigation

    constructor(private viewModel: CoreFrameDropDownTreeViewViewModel, private element?: JQuery) {
        super(viewModel, element);

        this.onWidgetCreated(() => {

            this.applyBindings(this.control, {
                controlName: CONTROL_NAME,
                tooltip: this.viewModel.selectionTooltipLabel,
                visible: this.viewModel.isVisible,
                invalid: this.viewModel.invalidationText,
                interact: this.viewModel.isEnabled,
                controlIdent: this.identifier,
                states: this.definePureComputed({
                    read: () => {
                        let states = [];
                        if (this.viewModel.isRequired() === true) states.push('required');
                        if (this.viewModel.isValid() === true) states.push('valid');
                        if (this.viewModel.isValid() !== true) states.push('invalid');
                        if (this.viewModel.isEnabled() === true) states.push('enabled');
                        if (this.viewModel.isEnabled() !== true) states.push('disabled');

                        return states;
                    }
                })
            });

            let $controlChildren = this.control.children();
            let $wrapper = $controlChildren.find('.c-dropdowntreeview-wrapper');
            this.applyBindings($wrapper, {
                autoLocator: this.context.locator
            });
        });
    }

    /**
     * @override
     */
    public getControl(): JQuery {
        if (this.control != null) {
            return this.control;
        }

        this.control = this.element || $('<div>');

        let timerId = this.applyTemplate(layout, this.control, this.applyTemplateCallback.bind(this));
        this.triggers[timerId].then(this.widget.bind(this, null));

        return this.control;
    }

    private applyTemplateCallback() {
        let $controlChildren = this.control.children();

        this.$popup = $controlChildren.find('.popup');
        let $area = $controlChildren.find('.area');
        let $icon = $controlChildren.find('.icon');
        this.$wrapper = $controlChildren.find('.c-dropdowntreeview-wrapper');

        this.$popup.css('display', 'none');
        this.addEventHandler('click', $area, this.areaClickHandler.bind(this));
        this.addEventHandler('click', $icon, this.areaClickHandler.bind(this));
        this.addEventHandler('click', $('body'), this.focusOutHandler.bind(this));
        this.addCapturedEventHandler('scroll', $('body'), this.scrollHandler.bind(this));

        $.when(this.fancyTreeControl.widgetIsCreated).then(() => {
            if (this.isDisposed)
                return;

            let fancytree = this.fancyTreeControl.getFancytree();
            let $fancytreeControl = this.fancyTreeControl.getControl();
            this.$fancytreeContainer = fancytree.$container;

            this.addEventHandler('focusin', this.$fancytreeContainer, () => { $fancytreeControl.addClass('focused'); });
            this.addEventHandler('focusout', this.$fancytreeContainer, () => { $fancytreeControl.removeClass('focused'); });

            this.applyBindings(this.$fancytreeContainer, {
                hotkey: {
                    shortcut: ['space', 'enter'],
                    handler: this.fancytreeSpaceCallback.bind(this, fancytree),
                    isGlobal: false
                }
            });
            this.applyBindings(this.$fancytreeContainer, {
                hotkey: {
                    shortcut: ['down'],
                    handler: this.fancytreeMoveDownCallback.bind(this, fancytree),
                    isGlobal: false
                }
            });
            this.applyBindings(this.$fancytreeContainer, {
                hotkey: {
                    shortcut: ['up'],
                    handler: this.fancytreeMoveUpCallback.bind(this, fancytree),
                    isGlobal: false
                }
            });

            this.defineSubscription(this.viewModel.activateNodeByKey, (key) => {
                let node = fancytree.getNodeByKey(key);

                if (!node) {
                    return;
                }

                if (node.parent && node.parent.isExpanded() == false) {
                    node.parent.setExpanded(true);
                }

                let isHidden = node.span.offsetParent === null;
                if (!isHidden) {
                    node.setActive(true);
                    this.$fancytreeContainer.focus();
                }
                else {
                    let subscription = this.viewModel.isExpanded.subscribe(isExpanded => {
                        if (isExpanded) {
                            if (key == this.viewModel.activateNodeByKey()) {
                                // node-variable contains old information if the node was moved or removed meanwhile
                                node = fancytree.getNodeByKey(key);
                                if (node) {
                                    node.setActive(true);
                                }
                                this.$fancytreeContainer.focus();
                            }
                            subscription.dispose();
                        }
                    });
                }

                if (node == fancytree.getFirstChild()) {
                    this.lastAction = 'movedToFirstNode'
                }
            });

            this.defineSubscription(this.viewModel.removeNodeByKeyObservable, (key) => {
                let node = fancytree.getNodeByKey(key);
                if (!node) {
                    return;
                }

                this.inRemovalState = true;

                let anotherNode = node.getPrevSibling() || node.getNextSibling();

                node.remove();

                if (anotherNode) {
                    this.viewModel.activateNodeByKey(anotherNode.key);
                }
                else {
                    this.$fancytreeContainer.focus();
                }

                this.inRemovalState = false;
            });

            this.defineSubscription(this.viewModel.isExpanded, (isExpanded) => {
                if (this.viewModel.isExpanded)
                    this.resizePopup();
            });
        });

        this.applyBindings(this.$wrapper, {
            hotkey: {
                shortcut: 'space',
                hanlder: this.wrapperSpaceCallback.bind(this),
                isGlobal: false
            }
        });
        this.applyBindings(this.$wrapper, {
            hotkey: {
                shortcut: 'down',
                hanlder: this.wrapperDownCallback.bind(this),
                isGlobal: false
            }
        });

        this.applyBindings($area,
            {
                loader: this.viewModel.isBusy
            });

        this.applyBindings(this.$popup,
            {
                loader: this.viewModel.isBusy,
                visible: this.viewModel.isExpanded,
                controlName: CONTROL_NAME + '-popup',
                locator: LocatorHelper.buildLocator(this.viewModel.locator, 'popup')
            });

        this.fancyTreeControl.renderNode = this.renderNode.bind(this);
        this.fancyTreeControl.activate = this.activate.bind(this);
        this.fancyTreeControl.dblclick = () => false;

        this.fancyTreeControl.expand = this.resizePopup.bind(this);
        this.fancyTreeControl.collapse = this.resizePopup.bind(this);
        this.$popup.detach().appendTo('body');
    }

    private resizePopup() {
        if (this.isDisposed)
            return;

        KendoDropDownHelper.resetTreeViewDropDownPosition(this.control, this.$popup, this.$fancytreeContainer);
    }

    private activate(event: JQueryEventObject, data: Fancytree.EventData): void {
        if (this.isDisposed)
            return;

        let fancytree = this.fancyTreeControl.getFancytree();
        let activeNode = fancytree.activeNode as Fancytree.FancytreeNode;
        let height = this.getNodeHeight();

        if (activeNode) {
            let viewTop = this.$popup.scrollTop();
            let viewHeight = this.$popup.height();
            let viewBottom = viewTop + viewHeight;

            let rowTop = activeNode.li.offsetTop;
            let rowHeight = height;
            let rowBottom = rowTop + rowHeight;

            if (rowTop < viewTop) {
                this.$popup.scrollTop(rowTop);
            }
            else if (rowBottom > viewBottom) {
                this.$popup.scrollTop(rowBottom - viewHeight);
            }
        }
    }

    private getNodeHeight() {
        let includeMargin = true;
        let height = 0;

        let tree = this.fancyTreeControl.getFancytree();

        return $(tree.findFirst(node => { return node.isExpanded() !== true || node.hasChildren() !== true }).li).outerHeight();
    }

    private fancytreeMoveUpCallback(fancytree: Fancytree.Fancytree) {
        if (this.isDisposed)
            return false;

        if (!this.viewModel.isExpanded()) {
            return false;
        }

        let activeNode = fancytree.activeNode;
        if (!activeNode) {
            return false;
        }

        fancytree.$container.focus();

        let firstNode = fancytree.getFirstChild();
        if (activeNode != firstNode) {
            this.lastAction = 'movedToAnotherNode';
            return false;
        }

        if (this.lastAction != 'movedToFirstNode') {
            this.lastAction = 'movedToFirstNode';
            return false;
        }

        this.viewModel.isExpanded(false);

        fancytree.activateKey(false);

        let $controlChildren = this.control.children();
        let $wrapper = $controlChildren.find('.c-dropdowntreeview-wrapper');
        $wrapper.focus();

        return false;
    }

    private fancytreeMoveDownCallback(fancytree: Fancytree.Fancytree) {
        if (this.isDisposed)
            return false;

        if (!this.viewModel.isExpanded()) {
            return false;
        }

        let activeNode = fancytree.activeNode;
        if (!activeNode) {
            return false;
        }

        this.lastAction = 'movedToAnotherNode';

        fancytree.$container.focus();

        return false;
    }

    private fancytreeSpaceCallback(fancytree) {
        if (this.isDisposed)
            return false;

        let activeNode = fancytree.activeNode;

        if (activeNode) {
            let nodeData = activeNode.data as NodeData;
            let checkBoxViewModel = nodeData.checkBoxViewModel;

            this.clickTitleHandler(checkBoxViewModel, activeNode);
        }

        return false;
    }

    private wrapperSpaceCallback() {
        if (this.isDisposed)
            return false;

        if (!this.viewModel.isEnabled()) {
            return false;
        }

        this.togglePopup();

        if (this.hasActiveNode()) {
            this.focusTree();
        }

        return false;
    }

    private wrapperDownCallback() {
        if (this.isDisposed)
            return false;

        if (!this.viewModel.isEnabled()) {
            return false;
        }

        if (!this.viewModel.isExpanded()) {
            this.togglePopup();
        }

        if (!this.hasActiveNode()) {
            this.selectFirstNode();
            this.lastAction = 'movedToFirstNode';
        }

        this.focusTree();


        return false;
    }

    private renderNode(event, data) {
        if (this.isDisposed)
            return;

        let node = data.node as Fancytree.FancytreeNode;
        let nodeData = node.data as NodeData;
        let $span = $((node as any).span);

        if (node.hasChildren()) {
            let isExpanded = node.isExpanded();
            $span.attr('data-isExpanded', String(isExpanded));
        }

        if (nodeData['isRendered'] === true || _.keys(nodeData).length == 0) {
            return;
        }

        let nodeLevel = node.getLevel();
        if (nodeLevel > 1) {
            let indent = (16 * (nodeLevel - 1));
            $span.css('padding-left', indent + 'px');
        }

        let $title = $span.find('> .fancytree-title');

        let $expander = $span.find('> .fancytree-expander');
        let $checkBoxWrapper = $('<span>');
        let $checkBoxContent = $('<input type="checkbox"><div class="element">');

        $checkBoxWrapper.append($checkBoxContent);
        $checkBoxWrapper.insertBefore($title);

        let checkBoxViewModel = nodeData.checkBoxViewModel;
        let cbC = new this.CheckBoxControl(checkBoxViewModel, $checkBoxWrapper);
        let _cbVMDispose = checkBoxViewModel.dispose.bind(checkBoxViewModel);
        // hier muss auch das Control disposed werden (Speicherleaks)
        checkBoxViewModel.dispose = () => {
            cbC.dispose();
            _cbVMDispose();
        }
        cbC.getControl();

        this.applyBindings($expander, {
            locator: this.defineComputed(() => {
                return checkBoxViewModel.locator() + '.treeitem-toggle';
            })
        });

        this.addEventHandler('click', $title, this.clickTitleHandler.bind(this, checkBoxViewModel, node));
        this.addEventHandler('click', $span, node.setActive.bind(node, true));

        nodeData['isRendered'] = true;
    }

    private clickTitleHandler(checkBoxViewModel: CheckBoxViewModelT, node: Fancytree.FancytreeNode, event?: JQueryEventObject): void {
        let isSingleSelect = this.viewModel.isMultiselect !== true;
        let isChecked = checkBoxViewModel.isChecked() === true;


        // in single-select mode hitting an already checked node again should not toggle it to 'unchecked'
        // this is because the user does not see the checkbox and gets his feedback of 'is it checked or not?' from the 
        // activeNode-highlighting which is still given in this szenario
        if (isSingleSelect && isChecked) {
            this.viewModel.isExpanded(false);
            return;
        }

        checkBoxViewModel.toggleIfEnabled();
    }

    /**
     * Wird vom Klick auf den body ausgelöst
     * Ist das geklickte Element null, gehört nicht zum Control oder zum Popup, so hat das Control den Focus verloren
     *
     * @param event focusOutEvent
     */
    private focusOutHandler(event: JQueryEventObject) {
        const target = event.target;

        const controlLostFocus = target == null || (!Utilities.isElementSameOrChildOf(target, this.$wrapper) && !Utilities.isElementSameOrChildOf(target, this.$popup));

        if (controlLostFocus && !this.inRemovalState) {
            this.viewModel.isExpanded(false);
        }
    }

    /**
     * Callback for scroll event on window
     * @param event
     */
    private scrollHandler(event: JQueryEventObject) {
        const target = event.target;

        const controlLostFocus = target == null || (!Utilities.isElementSameOrChildOf(target, this.$wrapper) && !Utilities.isElementSameOrChildOf(target, this.$popup));

        if (controlLostFocus) {
            this.viewModel.isExpanded(false);
        }
    }

    private areaClickHandler(event) {
        this.togglePopup();

        if (this.viewModel.isExpanded() && this.hasActiveNode()) {
            this.focusTree();
        }
    }

    private togglePopup() {
        if (document.activeElement.classList.contains('fancytree-container')) {
            return;
        }

        this.viewModel.isExpanded(!this.viewModel.isExpanded());
    }

    private selectFirstNode() {
        let fancytree = this.fancyTreeControl.getFancytree();

        if (fancytree.getFirstChild()) {
            fancytree.getFirstChild().setActive(true);
        }
    }

    private hasActiveNode(): boolean {
        let fancytree = this.fancyTreeControl.getFancytree();
        let activeNode = fancytree.activeNode;

        return !!activeNode;
    }

    private focusTree() {
        let fancytree = this.fancyTreeControl.getFancytree();
        fancytree.$container.focus();
    }

    protected disposeInternal() : void {
        this.activate['$popup'] = null;
        super.disposeInternal();
    }
}

interface NodeData extends Fancytree.NodeData {
    checkBoxViewModel?: CheckBoxViewModelT;
}

ComponentManager.setControl('CoreFrameDropDownTreeView', CoreFrameDropDownTreeView, 'RADPresentation.HTMLClient.DefaultViewExtensions');
ComponentManager.setControl('CoreFrameDropDownTreeViewControl', CoreFrameDropDownTreeView, 'RADPresentation.HTMLClient.DefaultViewExtensions');